import React from 'react'
import { motion } from 'framer-motion'
import { Star, Users, Calendar, ExternalLink, Music, Heart, MessageCircle } from 'lucide-react'
import { Artist } from '../../types'

interface ArtistCardProps {
  artist: Artist
  size?: 'small' | 'medium' | 'large'
  showActions?: boolean
}

const ArtistCard: React.FC<ArtistCardProps> = ({ artist, size = 'medium', showActions = true }) => {
  const [isFollowing, setIsFollowing] = React.useState(false)

  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-md',
    large: 'max-w-lg'
  }

  const imageHeights = {
    small: 'h-40',
    medium: 'h-48',
    large: 'h-64'
  }

  return (
    <motion.div
      className={`glass-card overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group ${sizeClasses[size]}`}
      whileHover={{ scale: 1.02 }}
      layout
    >
      <div className={`relative ${imageHeights[size]} overflow-hidden`}>
        <img
          src={artist.image}
          alt={artist.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        
        {artist.verified && (
          <div className="absolute top-4 right-4 w-10 h-10 bg-le-accent rounded-full flex items-center justify-center">
            <Star size={18} className="text-white fill-current" />
          </div>
        )}

        {/* Genre Tags */}
        <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
          {artist.genres.slice(0, 2).map((genre) => (
            <span
              key={genre}
              className="bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full"
            >
              {genre}
            </span>
          ))}
        </div>

        {/* Booking Status */}
        {artist.bookingInfo?.available && (
          <div className="absolute top-4 left-4 bg-green-500/80 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium">
            Available for Booking
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-xl text-white group-hover:text-le-accent transition-colors duration-300">
            {artist.name}
          </h3>
          <button className="text-white/60 hover:text-le-accent transition-colors duration-200">
            <ExternalLink size={18} />
          </button>
        </div>

        <p className="text-white/70 mb-4 leading-relaxed line-clamp-2">
          {artist.bio}
        </p>

        <div className="grid grid-cols-2 gap-4 text-sm text-white/60 mb-4">
          <div className="flex items-center space-x-2">
            <Users size={14} />
            <span>{artist.followers.toLocaleString()} followers</span>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar size={14} />
            <span>{artist.upcomingShows} shows</span>
          </div>
          <div className="flex items-center space-x-2">
            <Star size={14} className="text-yellow-400 fill-current" />
            <span>{artist.rating} rating</span>
          </div>
          <div className="flex items-center space-x-2">
            <Music size={14} />
            <span>{artist.location}</span>
          </div>
        </div>

        {artist.bookingInfo?.rate && (
          <div className="bg-le-accent/10 border border-le-accent/20 rounded-lg p-3 mb-4">
            <div className="text-sm text-white/70">Booking Rate</div>
            <div className="text-le-accent font-semibold">{artist.bookingInfo.rate}</div>
          </div>
        )}

        {showActions && (
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsFollowing(!isFollowing)
              }}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isFollowing
                  ? 'bg-le-accent text-white'
                  : 'bg-le-accent/20 text-le-accent hover:bg-le-accent hover:text-white'
              }`}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
            
            {artist.bookingInfo?.available && (
              <button className="flex-1 btn-secondary text-sm py-2">
                Book Now
              </button>
            )}
            
            <button className="p-2 bg-white/10 text-white/60 hover:text-white rounded-lg transition-all duration-200">
              <MessageCircle size={18} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default ArtistCard